import { useState } from 'react'
import { Link as RouterLink } from 'react-router'
import dayjs from 'dayjs'

import GlobalOutlinedIcon from '@ant-design/icons/GlobalOutlined'
import LinkOutlinedIcon from '@ant-design/icons/LinkOutlined'
import MailOutlinedIcon from '@ant-design/icons/MailOutlined'
import MedicineBoxOutlinedIcon from '@ant-design/icons/MedicineBoxOutlined'
import MessageOutlinedIcon from '@ant-design/icons/MessageOutlined'
import PhoneOutlinedIcon from '@ant-design/icons/PhoneOutlined'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Link from '@components/_mui/Link'
import Typography from '@components/_mui/Typography'

import useUserSetting from '@shared/hooks/src/useUserSetting'
import { getPhoneFormatted, toTitleCase, UserSettingName } from '@shared/utils'

import { usePatientDirectCall } from '@providers/VoiceCallProvider'
import { CBORuleSetDetailsDialog } from '@pages/CBOs'
import { ConversationDialog } from '@pages/Conversations/Conversation'
import Avatar from '@components/Avatar'
import { CBOCardPopper } from '@components/CBOCard'
import CopyToClipboard from '@components/CopyToClipboard'
import { UserCardPopper } from '@components/UserCard'

export default function PatientCard({ patient }) {
  return (
    <Paper variant="outlined" sx={styles.card}>
      <Stack sx={styles.body}>
        <Identity patient={patient} />
        <ContactLine patient={patient} />
        <Summary patient={patient} />
      </Stack>
    </Paper>
  )
}

function Identity({ patient }) {
  const [messageOpen, setMessageOpen] = useState(false)
  const { makeCall, disabled } = usePatientDirectCall(patient)

  const dob = patient.dob ? dayjs(patient.dob).format('L') : ''

  return (
    <Stack direction="row" sx={styles.identityRow}>
      <Stack direction="row" sx={styles.identityInfo}>
        <Avatar size="lg" user={patient} hover="card" />
        <Stack sx={{ minWidth: 0 }}>
          <Link component={RouterLink} to={`/app/patients/${patient.id}`} target="_blank">
            <Typography variant="h4" component="span">
              {patient.fullName},
            </Typography>
            <Typography component="span" sx={{ ml: 1, color: 'text.secondary', fontFamily: 'monospace' }}>
              MRN {patient.id}
            </Typography>
          </Link>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
            {patient.pronouns && <Typography sx={{ color: 'text.secondary' }}>{patient.pronouns}</Typography>}
            {patient.pronouns && dob && <Box sx={styles.dot} />}
            {dob && <Typography sx={{ color: 'text.secondary' }}>DOB {dob}</Typography>}
            {patient.treatmentType && (
              <>
                <Box sx={styles.dot} />
                <Typography sx={{ color: 'text.secondary' }}>{patient.treatmentType}</Typography>
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
      <Stack direction="row" sx={styles.identityActions}>
        <ConversationDialog user={patient} open={messageOpen} onClose={() => setMessageOpen(false)} />
        <IconButton onClick={() => setMessageOpen(true)}>
          <MessageOutlinedIcon style={{ fontSize: 22 }} />
        </IconButton>
        <IconButton onClick={makeCall} disabled={disabled}>
          <PhoneOutlinedIcon style={{ fontSize: 22 }} />
        </IconButton>
      </Stack>
    </Stack>
  )
}

function ContactLine({ patient }) {
  const contactMethodSetting = useUserSetting(patient, UserSettingName.ContactMethod)
  const phone = patient.phone ? getPhoneFormatted(patient.phone) : null

  return (
    <Stack direction="row" sx={styles.contactLine}>
      {phone && (
        <CopyToClipboard>
          {({ copy }) => (
            <Stack direction="row" onClick={() => copy(phone)} sx={styles.contactItem}>
              <PhoneOutlinedIcon style={{ fontSize: 16 }} />
              <Typography>{phone}</Typography>
            </Stack>
          )}
        </CopyToClipboard>
      )}
      {patient.email && (
        <CopyToClipboard>
          {({ copy }) => (
            <Stack direction="row" onClick={() => copy(patient.email)} sx={styles.contactItem}>
              <MailOutlinedIcon style={{ fontSize: 16 }} />
              <Typography>{patient.email}</Typography>
            </Stack>
          )}
        </CopyToClipboard>
      )}
      {patient.language && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <GlobalOutlinedIcon style={{ fontSize: 16 }} />
          <Typography>
            {patient.language}
            {contactMethodSetting && <> · Prefers {toTitleCase(contactMethodSetting.value)}</>}
          </Typography>
        </Stack>
      )}
      <Typography sx={{ color: patient.smsOptOut ? 'text.disabled' : 'success.main' }}>
        ● SMS opt-{patient.smsOptOut ? 'out' : 'in'}
      </Typography>
    </Stack>
  )
}

function Summary({ patient }) {
  const [cboOpen, setCboOpen] = useState(false)

  return (
    <Grid container spacing={3} sx={styles.summary}>
      {patient.homeAddress && (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <AddressCol title="Home address" data={patient.homeAddress} />
        </Grid>
      )}
      {patient.shippingAddress && (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <AddressCol title="Shipping" data={patient.shippingAddress} />
        </Grid>
      )}
      <Grid size={{ xs: 12, md: 4 }}>
        <Stack spacing={1}>
          <Typography variant="caption" sx={styles.label}>
            Care team
          </Typography>
          {patient.provider && (
            <UserCardPopper userId={patient.provider.userId || patient.provider.id}>
              <CareTeamMember name={patient.provider.fullName} avatar={<Avatar hover="none" size="xs" user={patient.provider} />} />
            </UserCardPopper>
          )}
          {patient.cbo && (
            <CBOCardPopper cboId={patient.cbo.id}>
              <CareTeamMember
                name={patient.cbo.name}
                avatar={<Avatar.CBO hover="none" size="xs" data={patient.cbo} />}
                action={
                  patient.cbo.id && (
                    <IconButton size="small" onClick={() => setCboOpen(true)} onMouseOver={(e) => e.stopPropagation()}>
                      <LinkOutlinedIcon style={{ fontSize: 14 }} />
                    </IconButton>
                  )
                }
              />
            </CBOCardPopper>
          )}
          {patient.pharmacy && (
            <CareTeamMember
              name={patient.pharmacy.name}
              avatar={
                <Avatar.Icon size="xs">
                  <MedicineBoxOutlinedIcon style={{ fontSize: 13 }} />
                </Avatar.Icon>
              }
            />
          )}
          <CBORuleSetDetailsDialog id={patient.cbo?.id} slide open={cboOpen} onClose={() => setCboOpen(false)} />
        </Stack>
      </Grid>
    </Grid>
  )
}

function AddressCol({ title, data }) {
  const { address, address2, city, state, zip } = data
  const cityLine = [city, state, zip].filter(Boolean).join(', ')
  const full = [address, address2, cityLine].filter(Boolean).join('\n')

  return (
    <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
      <Typography variant="caption" copy={full} sx={styles.label}>
        {title}
      </Typography>
      <Stack sx={{ alignItems: 'flex-start' }}>
        {address && (
          <Typography copy sx={{ whiteSpace: 'nowrap' }}>
            {address}
          </Typography>
        )}
        {address2 && (
          <Typography copy sx={{ whiteSpace: 'nowrap' }}>
            {address2}
          </Typography>
        )}
        {cityLine && (
          <Typography copy sx={{ whiteSpace: 'nowrap' }}>
            {cityLine}
          </Typography>
        )}
      </Stack>
    </Stack>
  )
}

function CareTeamMember({ name, avatar, action }) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      {avatar}
      <Typography noWrap sx={{ flex: 1, minWidth: 0 }}>
        {name}
      </Typography>
      {action}
    </Stack>
  )
}

PatientCard.Loading = function PatientCardLoading() {
  return (
    <Paper variant="outlined" sx={styles.card}>
      <Stack sx={styles.body}>
        <Stack direction="row" sx={styles.identityInfo}>
          <Skeleton variant="circular" width={52} height={52} />
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="h4">
              <Skeleton width={200} />
            </Typography>
            <Typography>
              <Skeleton width={140} />
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" sx={styles.contactLine}>
          <Typography>
            <Skeleton width={120} />
          </Typography>
          <Typography>
            <Skeleton width={180} />
          </Typography>
          <Typography>
            <Skeleton width={100} />
          </Typography>
        </Stack>
        <Grid container spacing={3} sx={styles.summary}>
          {[0, 1, 2].map((i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
              <Stack spacing={1}>
                <Typography variant="caption">
                  <Skeleton width={80} />
                </Typography>
                <Typography>
                  <Skeleton width={140} />
                </Typography>
                <Typography>
                  <Skeleton width={120} />
                </Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Stack>
    </Paper>
  )
}

const styles = {
  card: {
    height: '100%',
  },
  body: {
    gap: 1,
    p: 2,
  },
  identityRow: {
    gap: 2,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  identityInfo: {
    gap: 1.5,
    alignItems: 'center',
    minWidth: 0,
  },
  identityActions: {
    gap: 1,
    flexShrink: 0,
  },
  summary: {
    pt: 1,
    borderTop: '1px solid',
    borderColor: 'divider',
  },
  contactLine: {
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: 3,
    rowGap: 0,
    '& .anticon': { color: 'text.secondary' },
  },
  contactItem: {
    gap: 1,
    alignItems: 'center',
    cursor: 'pointer',
  },
  label: {
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: 'text.secondary',
    fontWeight: 600,
  },
  dot: {
    width: 4,
    height: 4,
    borderRadius: '50%',
    bgcolor: 'text.disabled',
    display: 'inline-block',
  },
}
